/* eslint-disable react-hooks/exhaustive-deps */
import { identify, load } from "@base/utils/analytics";
import { homeActions } from "@components/features/home/home.slice";
import { AuthDataProps } from "@components/features/home/home.type";
import { CurrentUserProps } from "@components/features/signIn/sign.type";
import { authorizationActions } from "@components/features/signIn/signIn.slice";
import { LoaderSnackbarWrapper } from "@components/organisms/LoaderSnackbarWrapper";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import { auth } from "@config/firebase/firbaseConfig";
import { AnalyticsSnippet } from "@segment/analytics-next";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  isSignInWithEmailLink,
  setPersistence,
} from "firebase/auth";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import {
  useAuthState,
  useSignInWithEmailLink,
} from "react-firebase-hooks/auth";
import { ConnectedProps, connect, useSelector } from "react-redux";

import "./App.css";
import { AppRouter } from "./App.router";
import { RootState } from "./store";

export interface AnalyticsWindow extends Window {
  analytics: AnalyticsSnippet;
}
declare let window: AnalyticsWindow;

type ReduxProps = ConnectedProps<typeof connector>;

const authData: string | null = localStorage.getItem("currentUser");
const REACT_APP_WEBHOOK_DELAY = process.env.REACT_APP_WEBHOOK_DELAY;
const webhookDelay = parseInt(REACT_APP_WEBHOOK_DELAY!, 10);

const App = ({ setLoader, setUser, getUser }: ReduxProps) => {
  const ancc = Cookies.get("ANCC");
  const [currentUser, setCurrentUser] = useState<CurrentUserProps>();
  const user = useSelector((state: RootState) => state.user.user);
  const [userAuth, loadingAuth, errorAuth] = useAuthState(auth);
  const [signInWithEmailLink, , loading, error] = useSignInWithEmailLink(auth);

  const getCurrentUser = async () => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem("emailForSignIn");
      if (!email) {
        email =
          window.prompt("Please provide your email for confirmation") || "";
      }
      await signInWithEmailLink(email, window.location.href);
    }
    if (!authData) {
      setUser({
        isLoading: false,
      });
    }
  };

  const getUserInfo = async () => {
    const response = await getUser();
    if (response?.meta?.requestStatus === "fulfilled") {
      const user = {
        firstName: response?.payload?.first_name,
        lastName: response?.payload?.last_name,
        email: response?.payload?.email,
        dob: response?.payload?.date_of_birth,
        profileImg: response?.payload?.profile_url,
        uuid: response?.payload?.id,
        isLoading: false,
      };
      // Convert user to JSON string before storing in localStorage
      setCurrentUser((current) => {
        const currentUser = { ...current, ...user };
        localStorage.setItem("currentUser", JSON.stringify(currentUser));
        return currentUser;
      });
    }
  };

  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
    }
    if (currentUser?.uuid) {
      setLoader({
        loader: false,
        isBlankLoader: false,
      });
    }
  }, [currentUser]);

  useEffect(() => {
    if (window.analytics && !window.analytics.initialized && ancc === "true") {
      load();
    }
  }, []);

  useEffect(() => {
    if (user.uuid) {
      identify(user);
    }
  }, [user]);

  useEffect(() => {
    if (authData) {
      const usr: CurrentUserProps = JSON.parse(authData);
      setCurrentUser({
        firstName: usr?.firstName,
        lastName: usr?.lastName,
        dob: usr?.dob,
        email: usr?.email,
        profileImg: usr?.profileImg || "",
        uuid: usr?.uuid,
        token: usr?.token,
        isLoading: usr?.isLoading,
      });
    } else {
      if (!loadingAuth && !loading) {
        if (userAuth) {
          userAuth.getIdToken().then((jwt) => {
            setLoader({
              loader: true,
              isBlankLoader: true,
            });
            setTimeout(() => {
              setUser({ token: `Bearer ${jwt}` });
              setCurrentUser({
                token: `Bearer ${jwt}`,
              });
              getUserInfo();
            }, webhookDelay);
          });
        } else {
          getCurrentUser();
        }
      }
      setLoader({
        loader: userAuth?.uid ? false : loadingAuth || loading,
        isBlankLoader: true,
      });
      if (error) {
        setLoader({
          alert: { isVisible: !!error, message: error.message, type: "error" },
        });
        setCurrentUser({
          email: undefined,
          isLoading: false,
          uuid: "",
          token: "",
        });
      }
      if (errorAuth) {
        setLoader({
          alert: {
            isVisible: !!error,
            message: errorAuth.message,
            type: "error",
          },
        });
      }
    }
  }, [
    loading,
    error,
    userAuth,
    loadingAuth,
    errorAuth,
    setLoader,
    setUser,
    authData,
  ]);

  return (
    <>
      <LoaderSnackbarWrapper />
      <AppRouter />
    </>
  );
};

const mapDispatchToProps = {
  setLoader: LoaderSnackbarWrapperActions.setGlobalAppearances,
  setUser: homeActions.setUser,
  getUser: authorizationActions.getUser,
};

const connector = connect(undefined, mapDispatchToProps);
const AppRedux = connector(App);

export { AppRedux as App };
